import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box } from '@mui/material';
import { getCustomerPortalEntitlements } from '../../api/entitlementsApi';
import { useCustomerPortal } from '../../portal/CustomerPortalContext';
import { CustomerPortalModulesSection } from '../../components/CustomerPortalModulesSection';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { Button } from '../../components/ui/Button';

export function CustomerPortalEntitlementsPage() {
  const { t } = useTranslation(['customers', 'features', 'common']);
  const { activeCustomerId, loading: portalLoading } = useCustomerPortal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!activeCustomerId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await getCustomerPortalEntitlements(activeCustomerId);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [activeCustomerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (portalLoading) {
    return <LoadingSkeleton type="Detail" />;
  }

  if (!activeCustomerId) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <PageHeader
          type="List"
          title={t('customers:portal.my_modules_title')}
          subtitle={t('customers:portal.my_modules_subtitle')}
        />
        <Alert severity="info">
          {t('customers:portal.my_modules_select_company')}
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <PageHeader
          type="List"
          title={t('customers:portal.my_modules_title')}
          subtitle={t('customers:portal.my_modules_subtitle')}
        />
        <LoadingSkeleton type="CardGrid" rows={3} sx={{ mb: 2.5 }} />
        <LoadingSkeleton type="Detail" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <PageHeader
          type="List"
          title={t('customers:portal.my_modules_title')}
          subtitle={t('customers:portal.my_modules_subtitle')}
        />
        <SectionCard variant="Danger" sx={{ mb: 0 }}>
          <Alert
            severity="error"
            sx={{ bgcolor: 'transparent', p: 0 }}
            action={(
              <Button variant="Ghost" onClick={load} sx={{ minHeight: 32 }}>
                {t('common:retry')}
              </Button>
            )}
          >
            {t('customers:portal.my_modules_load_failed')}
          </Alert>
        </SectionCard>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('customers:portal.my_modules_title')}
        subtitle={t('customers:portal.my_modules_subtitle')}
      />

      <Alert severity="info" sx={{ mb: 2.5 }}>
        {t('features:customerModulesUseBillingNotice')}
      </Alert>

      <CustomerPortalModulesSection />
    </Box>
  );
}
